import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaLongArrowAltLeft, FaRegEyeSlash } from "react-icons/fa";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ApiPost } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import Loading from "../../components/Loading";
import axios from "axios";

const ANONYMOUS_LOGIN_PASSWORD =
  process.env.REACT_APP_ANONYMOUS_LOGIN_PASSWORD || "";

export default function SignupPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isShow, setIsShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const SignupSchema = Yup.object().shape({
    firstName: Yup.string().required(t("validation.name_required")),
    lastName: Yup.string().required(t("validation.lastname_required")),
    email: Yup.string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
    password: Yup.string()
      .required(t("validation.password_required"))
      .min(6, t("validation.password_min")),
    terms: Yup.boolean().oneOf([true], t("validation.terms_required")),
  });

  const getUserCountryCode = async () => {
    try {
      const response = await axios.get("https://ipapi.co/json/");
      return response.data.country_code;
    } catch (error) {
      commonToast(error?.message, "error");
      return "LT";
    }
  };

  const getAnonymousAccessToken = async () => {
    const response = await ApiPost(
      "/auth/login/anonymous",
      { password: ANONYMOUS_LOGIN_PASSWORD },
      {},
      false
    );
    const accessToken = response?.data?.tokens?.accessToken;
    if (!accessToken) {
      throw new Error("Anonymous login failed");
    }
    return accessToken;
  };

  const handleSignup = async (values) => {
    setLoading(true);

    try {
      const countryCode = await getUserCountryCode();
      const accessToken = await getAnonymousAccessToken();

      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        preferredLanguage: i18n.language || "en",
        countryCode: countryCode || "LT",
        userPreferences: {
          preferVisionImpairmentMode: false,
          allowMarketingDataCollection: false,
          allowAnalyticsDataCollection: false,
        },
      };

      const response = await ApiPost(
        "/users/content-creators",
        payload,
        {},
        false,
        accessToken
      );
      const Data = response?.data;
      const createdId = Data?.userId || Data?.contentCreatorId;
      setLoading(false);
      if (createdId || response?.code === 200) {
        commonToast(t("signupmsg"), "success");
        navigate("/auth/confirm", { state: { email: values.email.trim() } });
      } else {
        commonToast(Data?.error || response?.error, "error");
      }
    } catch (error) {
      console.log("error", error);
      commonToast(
        error?.response?.data?.data?.error ||
          error?.data?.error ||
          error?.message,
        "error"
      );
      setLoading(false);
    }
  };

  return (
    <div className="bg-white max-w-4xl  mx-auto my-6 rounded-[32px] p-5 w-[434px] md:w-full">
      <div className="flex justify-start items-center">
        <Link
          to={`/auth/login`}
          className="border cursor-pointer rounded-full flex justify-center items-center size-10"
        >
          <FaLongArrowAltLeft />
        </Link>
        <div className="medium-text flex-1 text-center">{t("register")}</div>
      </div>

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          terms: false,
        }}
        validationSchema={SignupSchema}
        onSubmit={(values) => {
          handleSignup(values);
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <Field
              name="firstName"
              type="text"
              placeholder={t("name")}
              className={`regular-text border h-[56px] w-full mt-4 rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${errors.firstName && touched.firstName ? "border-red-500" : ""
                }`}
            />
            <ErrorMessage
              name="firstName"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            <Field
              name="lastName"
              type="text"
              placeholder={t("lastname")}
              className={`regular-text border h-[56px] w-full mt-4 rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${errors.lastName && touched.lastName ? "border-red-500" : ""
                }`}
            />
            <ErrorMessage
              name="lastName"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            <Field
              name="email"
              type="text"
              placeholder={t("email")}
              className={`regular-text border h-[56px] w-full mt-4 rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${errors.email && touched.email ? "border-red-500" : ""
                }`}
            />
            <ErrorMessage
              name="email"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            <div className="relative flex items-center  mt-4">
              <Field
                name="password"
                type={`${isShow ? "text" : "password"}`}
                placeholder={t("password")}
                className={`regular-text border h-[56px] w-full rounded-xl ps-5 shadow-inner outline-none placeholder:text-lightgray text-black ${errors.password && touched.password ? "border-red-500" : ""
                  }`}
              />
              {isShow ? (
                <MdOutlineRemoveRedEye
                  className="absolute cursor-pointer right-5 text-xl"
                  onClick={() => setIsShow(!isShow)}
                />
              ) : (
                <FaRegEyeSlash
                  className="absolute cursor-pointer right-5 text-xl"
                  onClick={() => setIsShow(!isShow)}
                />
              )}
            </div>
            <ErrorMessage
              name="password"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            <div className="flex items-center gap-2 base-text mt-6">
              <Field
                type="checkbox"
                name="terms"
                className="appearance-none text-white text-lg  flex items-center w-5 h-5 border shadow-inner rounded-lg checked:bg-darkblue checked:border-none"
              />
              <span>
                {t("agree")} <span className="text-darkblue">{t("rules")}</span>{" "}
                {t("and")} <span className="text-darkblue">{t("policy")}</span>
              </span>
            </div>
            <ErrorMessage
              name="terms"
              component="div"
              className="text-red-500 text-sm mt-1"
            />

            <button type="submit" className="primary-btn w-full mt-6">
              {loading ? <Loading /> : t("register")}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}
